import type { IMe, IPreferences, IRecentItem, IRecordLocation } from '@platform/core/types';
import { ApiError } from './ApiError';
import type { HttpClient } from './HttpClient';
import type { IInboxWireItem } from './IInboxWireItem';
import type { IMyWorkWire } from './IMyWorkWire';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Поточний користувач: профіль, вхідні, нещодавні, улюблені, налаштування. */
export class MeDao {
  constructor(private readonly http: HttpClient) {}

  me(): Promise<IMe> { return this.http.get('/me'); }
  inbox(unreadOnly = false): Promise<IInboxWireItem[]> { return this.http.get('/me/inbox', { unread: unreadOnly }); }
  unreadCount(): Promise<{ count: number }> { return this.http.get('/me/inbox/unread-count'); }
  /**
   * Позначити елементи вхідних прочитаними (порожньо — усі). Сервер попереднього формату приймає лише GUID збережених сповіщень (на ключі
   * обчислених елементів відповідає 400) — тоді запит повторюється лише з GUID.
   */
  async markRead(ids?: string[]): Promise<void> {
    try {
      await this.http.post('/me/inbox/read', ids ?? []);
    } catch (e) {
      const guids = (ids ?? []).filter((id) => GUID.test(id));
      if (!(e instanceof ApiError) || e.status !== 400 || !guids.length || guids.length === ids?.length) throw e;
      await this.http.post('/me/inbox/read', guids);
    }
  }
  /** Позначити елементи непрочитаними (POST /me/inbox/unread); сервер без цієї дії (404 / 405) — тихо, стан лишається локальним. */
  async markUnread(ids: string[]): Promise<void> {
    try {
      await this.http.post('/me/inbox/unread', ids);
    } catch (e) {
      if (!(e instanceof ApiError) || (e.status !== 404 && e.status !== 405)) throw e;
    }
  }
  /** «Моя робота» простору: призначені мені відкриті записи всіх модулів і лічильники меню (GET /me/work?workspaceId=). */
  work(workspaceId: string): Promise<IMyWorkWire> { return this.http.get('/me/work', { workspaceId }); }
  recent(): Promise<IRecentItem[]> { return this.http.get('/me/recent'); }
  favourites(): Promise<IRecentItem[]> { return this.http.get('/me/favourites'); }
  setFavourite(recordId: string, on: boolean): Promise<void> {
    return on ? this.http.put(`/me/favourites/${recordId}`) : this.http.delete(`/me/favourites/${recordId}`);
  }
  updatePreferences(body: IPreferences): Promise<IPreferences> { return this.http.put('/me/preferences', body); }
  /** Робочий простір, модуль і тип видимого користувачу запису (GET /me/records/{id}/location; невидимий — 404, як і неіснуючий). */
  recordLocation(recordId: string): Promise<IRecordLocation> { return this.http.get(`/me/records/${encodeURIComponent(recordId)}/location`); }
}
